import { get, noop } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { EmailInput } from '~/eds/src/components/EmailInput/EmailInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Panel_DEPRECATED } from '~/eds/src/components/Panel/Panel_DEPRECATED';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { testIsEmailValid } from '~/eds/src/utils/testIsEmailValid';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { CreateUserResponse } from '~/redux/api/methods/roles/roles';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';

import DepartmentSelect from '../Shared/DepartmentSelect/DepartmentSelect';
import { showToast } from '../Shared/EcToast/showToast';

type Props = {
  visible: boolean;
  onHide: () => void;
};

export const UserFormPanel = ({ visible, onHide }: Props) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);

  const [createUser, createUserResult] =
    api.endpoints.createSuperAdmin.useMutation();
  const {
    isLoading: isLoadingCreate,
    isError: isErrorCreate,
    isSuccess: isSuccessCreate,
    data: dataCreate,
    error: errorCreate,
  } = createUserResult;
  const [sendInvitationEmail] = api.endpoints.sendInvitationEmail.useMutation();

  const [firstName, setFirstName] = useState<Nullable<string>>('');
  const [lastName, setLastName] = useState<Nullable<string>>('');
  const [email, setEmail] = useState<Nullable<string>>('');
  const [jobTitle, setJobTitle] = useState<Nullable<string>>('');
  const [departments, setDepartments] = useState<Nullable<number[]>>([]);
  const [emailInstructions, setEmailInstructions] =
    useState<Nullable<boolean>>(true);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setJobTitle('');
    setDepartments([]);
    setEmailInstructions(true);
  };

  const handleHide = () => {
    reset();
    onHide();
  };
  const handleSubmit = () => {
    createUser({
      first_name: firstName!,
      last_name: lastName!,
      email: email!,
      job_title: jobTitle,
      departments: departments ?? [],
      client: currentUser?.client,
    });
  };

  const handleSuccess = (
    successData?: CreateUserResponse,
    successMessage?: string,
  ) => {
    showToast(SUCCESS, successMessage);
    sendInvitationEmail({
      userId: get(successData, 'data.id', ''),
      enableRolodex: isRolodexDataSourceEnabled,
    });
    handleHide();
  };

  const successMessage = intl.formatMessage(
    {
      id: 'SuperAdminUserFormPanel.userCreatedSuccess',
      defaultMessage: 'User {firstName} created successfully!',
      description:
        'Success message when super admin user is created. Example: firstName = "John"',
    },
    { firstName },
  );

  const optionalFieldLabel = intl.formatMessage({
    id: 'SuperAdminUserFormPanel.optionalLabel',
    defaultMessage: '(Optional)',
    description: 'Label indicating field is optional',
  });
  useEffect(() => {
    if (isSuccessCreate) {
      handleSuccess(dataCreate, successMessage);
    }
  }, [isSuccessCreate, dataCreate]);

  const showErrorMessage = (
    error: unknown,
    message = intl.formatMessage({
      id: 'SuperAdminUserFormPanel.defaultErrorMessage',
      defaultMessage: 'Something went wrong. Please try again later.',
      description: 'Default error message for super admin user operations',
    }),
  ) => {
    const errorMessage = get(
      error,
      'response.data.non_field_errors[0]',
      get(error, 'response.data.email[0]', message),
    );
    showToast(ERROR, errorMessage);
  };

  useEffect(() => {
    if (isErrorCreate) {
      showErrorMessage(errorCreate);
    }
  }, [isErrorCreate, errorCreate]);

  const isSubmitDisabled = !firstName || !lastName || !testIsEmailValid(email);
  const title = intl.formatMessage({
    id: 'SuperAdminUserFormPanel.newUserTitle',
    defaultMessage: 'New User',
    description: 'Title for creating a new super admin user',
  });
  const subtitle = intl.formatMessage({
    id: 'SuperAdminUserFormPanel.newUserSubtitle',
    defaultMessage: 'Create a new user in your organization.',
    description: 'Subtitle for new super admin user form',
  });
  const children = (
    <Layout direction="column" spacing="2">
      <Text>{subtitle}</Text>
      <Text variant="section">
        {intl.formatMessage({
          id: 'SuperAdminUserFormPanel.generalInformationSection',
          defaultMessage: 'General Information',
          description: 'Section header for general information',
        })}
      </Text>
      <FormField<string, false>
        placeholder={intl.formatMessage({
          id: 'SuperAdminUserFormPanel.firstNamePlaceholder',
          defaultMessage: 'First name',
          description: 'Placeholder for first name field',
        })}
        name="firstName"
        value={firstName}
        onChange={setFirstName}
        input={TextInput}
        label={intl.formatMessage({
          id: 'SuperAdminUserFormPanel.firstNameLabel',
          defaultMessage: 'First name',
          description: 'Label for first name field',
        })}
      />

      <FormField<string, false>
        placeholder={intl.formatMessage({
          id: 'SuperAdminUserFormPanel.lastNamePlaceholder',
          defaultMessage: 'Last name',
          description: 'Placeholder for last name field',
        })}
        name="lastName"
        value={lastName}
        onChange={setLastName}
        input={TextInput}
        label={intl.formatMessage({
          id: 'SuperAdminUserFormPanel.lastNameLabel',
          defaultMessage: 'Last name',
          description: 'Label for last name field',
        })}
      />

      <Layout direction="column" spacing="1">
        <FormField<string, false>
          placeholder={intl.formatMessage({
            id: 'SuperAdminUserFormPanel.emailPlaceholder',
            defaultMessage: 'Email',
            description: 'Placeholder for email field',
          })}
          name="email"
          value={email}
          onChange={setEmail}
          input={EmailInput}
          label={intl.formatMessage({
            id: 'SuperAdminUserFormPanel.emailLabel',
            defaultMessage: 'Email',
            description: 'Label for email field',
          })}
        />
        <FormField<boolean, false>
          name="sign-in-instructions'"
          value={emailInstructions}
          onChange={setEmailInstructions}
          inputProps={{
            option: {
              label: intl.formatMessage({
                id: 'SuperAdminUserFormPanel.emailInstructionsCheckbox',
                defaultMessage: 'Email sign-in instructions',
                description: 'Checkbox label for emailing sign-in instructions',
              }),
              value: 'sign-in-instructions',
            },
          }}
          input={Checkbox}
          label={''}
        />
      </Layout>

      <Layout direction="column" spacing="1">
        <Text variant="body-bold">
          {intl.formatMessage({
            id: 'SuperAdminUserFormPanel.jobTitleLabel',
            defaultMessage: 'Job title',
            description: 'Label for job title field',
          })}{' '}
          <Text variant="tiny" color="text.secondary">
            {optionalFieldLabel}
          </Text>
        </Text>
        <FormField<string, false>
          placeholder={intl.formatMessage({
            id: 'SuperAdminUserFormPanel.jobTitlePlaceholder',
            defaultMessage: 'Job title',
            description: 'Placeholder for job title field',
          })}
          name="jobTitle"
          value={jobTitle}
          onChange={setJobTitle}
          input={TextInput}
          label=""
        />
      </Layout>

      <Layout direction="column" spacing="1">
        <Text variant="body-bold">
          {intl.formatMessage({
            id: 'SuperAdminUserFormPanel.departmentLabel',
            defaultMessage: 'Department',
            description: 'Label for department field',
          })}{' '}
          <Text variant="tiny" color="text.secondary">
            {optionalFieldLabel}
          </Text>
        </Text>
        <Text color="text.secondary">
          {intl.formatMessage({
            id: 'SuperAdminUserFormPanel.departmentDescription',
            defaultMessage:
              'Select a department for organizing purposes only. Use groups to grant access to documents and workflows.',
            description: 'Description for department field',
          })}
        </Text>

        <FormField<number[], false>
          name="departments"
          value={departments}
          onChange={setDepartments}
          input={DepartmentSelect}
          label=""
          inputProps={{
            width: 'fullWidth',
            isMulti: true,
          }}
        />
      </Layout>

      <Layout direction="column" spacing="2" mt="6">
        <Text variant="section">
          {intl.formatMessage({
            id: 'SuperAdminUserFormPanel.permissionsSection',
            defaultMessage: 'Permissions',
            description: 'Section header for permissions',
          })}
        </Text>

        <Layout direction="column" spacing="1">
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'SuperAdminUserFormPanel.roleLabel',
              defaultMessage: 'Role',
              description: 'Label for role field',
            })}
          </Text>
          <Text color="text.secondary">
            {intl.formatMessage({
              id: 'SuperAdminUserFormPanel.roleDescription',
              defaultMessage:
                'Assign a role to the user to grant permissions in Evisort.',
              description: 'Description for role field',
            })}
          </Text>
          <Select
            disabled={true}
            placeholder={intl.formatMessage({
              id: 'SuperAdminUserFormPanel.rolePlaceholder',
              defaultMessage: 'Select a role',
              description: 'Placeholder for role selection',
            })}
            options={[
              {
                label: intl.formatMessage({
                  id: 'SuperAdminUserFormPanel.superAdminRole',
                  defaultMessage: 'Super Admin',
                  description: 'Super Admin role option',
                }),
                value: '1',
              },
            ]}
            name="role"
            value={'1'}
            onChange={noop}
            isMulti={false}
          />
        </Layout>
      </Layout>
    </Layout>
  );

  if (enableModalConsolidation && visible) {
    return (
      <Panel
        title={title}
        enableBackdrop
        mode="floating"
        width="560px"
        hidden={{ onHide: handleHide }}
        footer={{
          actions: [
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'SuperAdminUserFormPanel.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: handleHide,
            },
            {
              mode: 'chip',
              level: 'primary',
              text: intl.formatMessage({
                id: 'SuperAdminUserFormPanel.saveButton',
                defaultMessage: 'Save',
                description: 'Save button text',
              }),
              onClick: handleSubmit,
              isLoading: isLoadingCreate,
              disabled: isSubmitDisabled,
            },
          ],
        }}
        children={children}
      />
    );
  }

  return (
    <Panel_DEPRECATED
      width="m"
      isVisible={visible}
      title={title}
      onHide={handleHide}
      isPadded={true}
      mode="blocking"
      footer={
        <Layout spacing="2" justify="flex-end" borderTop="border" p="3">
          <Button
            variant="tertiary"
            text={intl.formatMessage({
              id: 'SuperAdminUserFormPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            onClick={handleHide}
          />
          <Button
            variant="primary"
            text={intl.formatMessage({
              id: 'SuperAdminUserFormPanel.saveButton',
              defaultMessage: 'Save',
              description: 'Save button text',
            })}
            onClick={handleSubmit}
            isLoading={isLoadingCreate}
            disabled={isSubmitDisabled}
          />
        </Layout>
      }
    >
      {children}
    </Panel_DEPRECATED>
  );
};
